'use client'

import { useGetCookie } from '@repo/cookie/client'
import { COOKIE_I18N_USER_COUNTRY } from '@repo/cookie/config'
import { markets } from '@repo/markets/markets'
import { LocaleIdentifier } from '@repo/markets/types'
import { hasInStorage, LOCAL_STORAGE_LOCALE_DIALOG, setInStorage } from '@repo/storage/service'
import { Button } from '@repo/ui/components/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@repo/ui/components/dialog'
import { FunctionComponent, useEffect } from 'react'
import useLocaleChange from '../../hooks/useLocaleChange.hook'
import useLocalePrefetch from '../../hooks/useLocalePrefetch.hook'
import { DIALOG, useDialog } from '../../providers/DialogProvider'
import { useSheet } from '../../providers/SheetProvider'
import { PageDto } from '../../types/page.types'
import LocaleButtons from '../navigation/LocaleButtonGroup'

interface Props {
  pageDto: PageDto
}

const LocaleDialog: FunctionComponent<Props> = ({ pageDto }) => {
  const { openSheetId } = useSheet()
  const getCookie = useGetCookie()
  const { openDialogIds, openDialog, isDialogOpen, closeDialog } = useDialog()
  const country = getCookie(COOKIE_I18N_USER_COUNTRY)
  const { prefetch } = useLocalePrefetch(pageDto.params.locale)
  const { changeLocale, isLoading } = useLocaleChange(pageDto.params.locale)

  // On load
  useEffect(() => {
    if (openDialogIds.size === 0 && openSheetId === null && country && !hasInStorage(LOCAL_STORAGE_LOCALE_DIALOG)) {
      const marketForCountry = markets.find((market) => market.countries?.includes(country))
      const showDialog = marketForCountry === undefined ? !pageDto.marketInstance.isDefault : marketForCountry.id !== pageDto.marketInstance.id

      if (showDialog) {
        openDialog(DIALOG.LOCALE)
        prefetch()
      }
    }
  }, [country, pageDto.marketInstance.id, pageDto.marketInstance.isDefault, openDialog, openDialogIds, openSheetId, prefetch])

  const handleOpenChange = (open: boolean) => {
    setInStorage(LOCAL_STORAGE_LOCALE_DIALOG, Date.now())

    if (!open) {
      closeDialog(DIALOG.LOCALE)
    }
  }

  const handleLocaleChange = (newLocale: LocaleIdentifier) => {
    handleOpenChange(false)
    changeLocale(newLocale)
  }

  return (
    <Dialog open={isDialogOpen(DIALOG.LOCALE)} onOpenChange={handleOpenChange} modal={false}>
      <DialogContent position='center'>
        <DialogHeader>
          <DialogTitle>{pageDto.dictionary?.locale?.popuptitle || 'Welcome to Miista'}</DialogTitle>
          <DialogDescription>{pageDto.dictionary?.locale?.popupcopy || 'You might be in the wrong region. Choose the currency you want to shop in.'}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant='ghostStatic' onClick={() => handleOpenChange(false)}>
            {pageDto.dictionary?.locale?.stayhere || 'Stay here'}
          </Button>
          <LocaleButtons
            locale={pageDto.params.locale}
            isLoading={isLoading}
            showCurrentLocale={false}
            onChange={handleLocaleChange}
            buttonVariantCurrentLocale='defaultStatic'
            buttonVariantOtherLocale='defaultStatic'
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default LocaleDialog
